import React, { useState, useEffect, useCallback } from 'react';
import {
  Mic, Plus, RefreshCw, ChevronLeft, Video, CheckCircle, Clock,
  AlertCircle, UserPlus, Save, Send, BookOpen, Archive, X, RotateCcw, Trash2,
} from 'lucide-react';
import { neonQuery, kcStartInterview, kcIngestTranscript, kcPublishDraft } from '../services/api';
import { getCurrentUser } from '../services/auth';

// Audience presets for the in-app Knowledge Base. NONE of these reach the
// customer-facing Jimmy — that's a separate, explicit opt-in below.
const AUDIENCE_PRESETS = [
  { key: 'dev', label: 'Devs only', value: ['dev'] },
  { key: 'operator', label: 'Staff (operators)', value: ['operator'] },
  { key: 'both', label: 'Everyone internal', value: ['dev', 'operator'] },
];
const FOLDERS = [
  { value: 'references/institutional', label: 'Reference — institutional knowledge' },
  { value: 'references', label: 'Reference — technical' },
  { value: 'projects', label: 'Project context' },
  { value: 'handoffs/runbooks', label: 'Runbook / how-to (operator-friendly)' },
];

const STATUS_BADGE = {
  queued: { label: 'Queued', cls: 'badge', icon: Clock },
  in_progress: { label: 'In progress', cls: 'badge badge-warning', icon: Video },
  awaiting_review: { label: 'Awaiting review', cls: 'badge badge-warning', icon: AlertCircle },
  published: { label: 'Published', cls: 'badge badge-success', icon: CheckCircle },
  archived: { label: 'Archived', cls: 'badge', icon: Archive },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.queued;
  const Icon = s.icon;
  return (
    <span className={s.cls} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

export default function KnowledgeCapture() {
  const user = getCurrentUser();
  const myId = user?.id != null ? parseInt(user.id, 10) : null;

  const [interviews, setInterviews] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState('all'); // all | mine | review
  const [showPublished, setShowPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedId, setSelectedId] = useState(null);

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await neonQuery('knowledge_interviews_list', {});
      setInterviews(Array.isArray(rows) ? rows : []);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStaff = useCallback(async () => {
    try {
      const rows = await neonQuery('users_list', {});
      const active = (Array.isArray(rows) ? rows : []).filter(
        (u) => !u.deleted_at && (u.is_active === true || String(u.is_active) === '1' || String(u.is_active) === 'true')
      );
      setStaff(active);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadInterviews(); loadStaff(); }, [loadInterviews, loadStaff]);

  const assignInterview = async (interviewId, adminIdStr) => {
    const a = staff.find((s) => String(s.admin_id) === String(adminIdStr));
    // optimistic UI: reflect the change immediately
    setInterviews((prev) => prev.map((i) => i.id === interviewId
      ? { ...i, assignee_admin_id: a ? parseInt(a.admin_id, 10) : null, assignee_name: a ? a.staff_name : null }
      : i));
    try {
      await neonQuery('knowledge_interview_assign', {
        id: interviewId,
        assignee_admin_id: a ? parseInt(a.admin_id, 10) : null,
        assignee_name: a ? a.staff_name : null,
        assignee_email: a ? a.email : null,
        assigned_by_name: user?.staffName || null,
      });
      loadInterviews();
    } catch (e) { setError(e.message); loadInterviews(); }
  };

  const publishedCount = interviews.filter((i) => i.status === 'published').length;
  const visible = interviews.filter((i) => {
    if (!showPublished && i.status === 'published') return false;
    if (filter === 'mine') return i.assignee_admin_id === myId;
    if (filter === 'review') return i.status === 'awaiting_review';
    return true;
  });

  if (selectedId) {
    return (
      <InterviewDetail
        interviewId={selectedId}
        staff={staff}
        user={user}
        onBack={() => { setSelectedId(null); loadInterviews(); }}
        onChanged={loadInterviews}
      />
    );
  }

  return (
    <div className="page">
      <div className="breadcrumb">
        <span>Configure Site</span> <span>/</span> <span>Knowledge Capture</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <Mic size={22} color="var(--orange)" /> Knowledge Capture
          </h1>
          <p style={{ color: '#667', margin: '4px 0 0' }}>
            Interview people on-camera, then review what the AI captured before it lands in the internal Knowledge Base.
            Nothing here reaches Jimmy unless you explicitly share it.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={loadInterviews}><RefreshCw size={16} /> Refresh</button>
      </div>

      <NewInterviewForm staff={staff} user={user} onCreated={loadInterviews} />

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[
            { k: 'all', label: 'All' },
            { k: 'mine', label: 'Assigned to me' },
            { k: 'review', label: 'Awaiting review' },
          ].map((t) => (
            <button
              key={t.k}
              className={filter === t.k ? 'btn btn-primary' : 'btn btn-outline'}
              style={{ padding: '4px 12px' }}
              onClick={() => setFilter(t.k)}
            >
              {t.label}
            </button>
          ))}
          {publishedCount > 0 && (
            <button
              className={showPublished ? 'btn btn-primary' : 'btn btn-outline'}
              style={{ padding: '4px 12px', marginLeft: 'auto' }}
              onClick={() => setShowPublished((v) => !v)}
            >
              {showPublished ? `Hide published (${publishedCount})` : `Show published (${publishedCount})`}
            </button>
          )}
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {loading ? (
            <p>Loading…</p>
          ) : visible.length === 0 ? (
            <p style={{ color: '#889' }}>No interviews here yet. Create one above to get started.</p>
          ) : (
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Topic</th><th>Assigned to</th><th>Status</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((i) => (
                  <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(i.id)}>
                    <td>
                      <strong>{i.title}</strong>
                      {i.source === 'follow_up' && <span className="badge" style={{ marginLeft: 6 }}>follow-up</span>}
                      {i.focus && <div style={{ fontSize: 12, color: '#889' }}>{i.focus.slice(0, 90)}{i.focus.length > 90 ? '…' : ''}</div>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="form-select"
                        style={{ minWidth: 150, padding: '4px 8px' }}
                        value={i.assignee_admin_id != null ? String(i.assignee_admin_id) : ''}
                        onChange={(e) => assignInterview(i.id, e.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {staff.map((s) => (
                          <option key={s.admin_id} value={String(s.admin_id)}>{s.staff_name}</option>
                        ))}
                      </select>
                    </td>
                    <td><StatusBadge status={i.status} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{i.created_at ? new Date(i.created_at).toLocaleDateString() : ''}</td>
                    <td><ChevronLeft size={16} style={{ transform: 'rotate(180deg)' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function NewInterviewForm({ staff, user, onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [focus, setFocus] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const reset = () => { setTitle(''); setFocus(''); setAssigneeId(''); setErr(''); };

  const submit = async () => {
    if (!title.trim()) { setErr('Give the interview a topic title.'); return; }
    setSaving(true); setErr('');
    try {
      const a = staff.find((s) => String(s.admin_id) === String(assigneeId));
      await neonQuery('knowledge_interview_create', {
        title: title.trim(),
        focus: focus.trim() || null,
        assignee_admin_id: a ? parseInt(a.admin_id, 10) : null,
        assignee_name: a ? a.staff_name : null,
        assignee_email: a ? a.email : null,
        created_by: user?.id != null ? parseInt(user.id, 10) : null,
        created_by_name: user?.staffName || null,
        source: 'manual',
      });
      reset(); setOpen(false);
      onCreated();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-primary" onClick={() => setOpen(true)}>
        <Plus size={16} /> New interview
      </button>
    );
  }

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>New interview</span>
        <button className="btn btn-ghost" onClick={() => { reset(); setOpen(false); }}><X size={16} /></button>
      </div>
      <div className="card-body">
        {err && <div className="alert alert-danger">{err}</div>}
        <div className="form-group">
          <label className="form-label">Topic</label>
          <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Company backstory & history" />
        </div>
        <div className="form-group">
          <label className="form-label">Focus (what the interviewer should dig into)</label>
          <textarea className="form-textarea" value={focus} onChange={(e) => setFocus(e.target.value)} rows={3}
            placeholder="e.g. How the company started, the first online sale, notable milestones, the Crisfield connection." />
        </div>
        <div className="form-group">
          <label className="form-label">Assign to</label>
          <select className="form-select" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">Me ({user?.staffName || 'current user'})</option>
            {staff.map((s) => (
              <option key={s.admin_id} value={s.admin_id}>
                {s.staff_name}{s.email ? ` (${s.email})` : ''}
              </option>
            ))}
          </select>
          <small style={{ color: '#889' }}>If you assign someone else, they get an email and see it under "Assigned to me."</small>
        </div>
        <button className="btn btn-primary" disabled={saving} onClick={submit}>
          {saving ? 'Creating…' : 'Create interview'}
        </button>
      </div>
    </div>
  );
}

function InterviewDetail({ interviewId, staff, user, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [starting, setStarting] = useState(false);
  const [convUrl, setConvUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await neonQuery('knowledge_interview_get', { id: interviewId });
      const row = Array.isArray(rows) ? rows[0] : rows;
      setData(row);
      if (row?.interview?.conversation_url && row.interview.status === 'in_progress') {
        setConvUrl(row.interview.conversation_url);
      }
      setErr('');
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [interviewId]);

  useEffect(() => { load(); }, [load]);

  // While a call is live, poll so the screen flips to the review view as soon
  // as the webhook lands the transcript.
  useEffect(() => {
    if (!convUrl) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [convUrl, load]);

  const interview = data?.interview;
  const draft = data?.draft;

  const start = async () => {
    setStarting(true); setErr('');
    try {
      const res = await kcStartInterview(interviewId, user?.staffName);
      setConvUrl(res.conversation_url);
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setStarting(false);
    }
  };

  const [fetching, setFetching] = useState(false);
  // "I've finished" button: actively PULL the transcript from Tavus + build the
  // draft (don't just re-check Neon — the webhook can miss), then keep checking
  // until the draft lands so one click is enough regardless of timing.
  const checkTranscript = async () => {
    setFetching(true); setErr('');
    try { await kcIngestTranscript(interviewId); } catch (e) { setErr(e.message); }
    // Poll for the draft for up to ~90s (Tavus finalize + Claude extraction).
    let landed = false;
    for (let n = 0; n < 18; n++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const rows = await neonQuery('knowledge_interview_get', { id: interviewId });
        const row = Array.isArray(rows) ? rows[0] : rows;
        if (row?.interview?.status === 'awaiting_review' && row?.draft) { setData(row); landed = true; break; }
      } catch (_) { /* keep polling */ }
    }
    setFetching(false);
    if (!landed) setErr('Still waiting on the transcript from Tavus. End the call inside the video if you haven\'t, then click again in a few seconds.');
  };

  const reRecord = async () => {
    if (!window.confirm('Discard any captured draft and let you record this interview again from scratch?')) return;
    setErr('');
    try {
      await neonQuery('knowledge_interview_reset', { id: interviewId });
      setConvUrl(null);
      await load();
      onChanged && onChanged();
    } catch (e) { setErr(e.message); }
  };

  const remove = async () => {
    if (!window.confirm('Delete the ENTIRE interview, including its topic and questions? This cannot be undone.\n\nIf you only want to throw away a bad recording and redo it, click Cancel and use "Discard draft & re-record" instead.')) return;
    try {
      await neonQuery('knowledge_interview_delete', { id: interviewId });
      onBack();
    } catch (e) { setErr(e.message); }
  };

  // Only blank to a full-page loader on the FIRST load. On background refreshes
  // (the status poll, the post-start refresh) keep the page — and the live video
  // iframe — mounted, or the refresh tears the call down mid-conversation.
  if (loading && !data) return <div className="page"><p>Loading…</p></div>;
  if (!interview) return <div className="page"><button className="btn btn-ghost" onClick={onBack}><ChevronLeft size={16} /> Back</button><p>Not found.</p></div>;

  return (
    <div className="page">
      <button className="btn btn-ghost" onClick={onBack}><ChevronLeft size={16} /> Back to interviews</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
        <h1 style={{ margin: 0 }}>{interview.title}</h1>
        <StatusBadge status={interview.status} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {interview.status !== 'queued' && (
            <button className="btn btn-outline" onClick={reRecord} title="Throw away the recorded draft and record this interview again. Keeps the interview and its questions.">
              <RotateCcw size={16} /> Discard draft &amp; re-record
            </button>
          )}
          <button className="btn btn-ghost" onClick={remove} title="Permanently remove the entire interview, including its questions" style={{ color: 'var(--orange)' }}>
            <Trash2 size={16} /> Delete entire interview
          </button>
        </div>
      </div>
      {interview.focus && <p style={{ color: '#556' }}>{interview.focus}</p>}
      {err && <div className="alert alert-danger">{err}</div>}

      {/* Carried-over questions from a parent interview */}
      {Array.isArray(interview.open_questions) && interview.open_questions.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-header">Questions to cover</div>
          <div className="card-body">
            <ul style={{ margin: 0 }}>
              {interview.open_questions.map((q, idx) => <li key={idx}>{q}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Live / start view */}
      {(interview.status === 'queued' || interview.status === 'in_progress') && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">Interview</div>
          <div className="card-body">
            {!convUrl ? (
              <>
                <p>When you click Start, you'll talk with an on-screen interviewer for about 20 minutes. Speak naturally — it's fine to ramble or stumble.</p>
                <button className="btn btn-primary" disabled={starting} onClick={start}>
                  <Video size={16} /> {starting ? 'Starting…' : 'Start interview'}
                </button>
              </>
            ) : (
              <>
                <div style={{ position: 'relative', width: '100%', maxWidth: 900, aspectRatio: '16 / 9', margin: '0 auto', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                  <iframe
                    title="Knowledge interview"
                    src={convUrl}
                    allow="camera; microphone; fullscreen; autoplay; display-capture"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </div>
                <p style={{ marginTop: 12, color: '#556' }}>
                  When you're done, <strong>end the call inside the video</strong> so the transcript finalizes. It'll show up here for review within a minute or two.
                </p>
                <button className="btn btn-outline" onClick={checkTranscript} disabled={fetching}>
                  <RefreshCw size={16} /> {fetching ? 'Fetching transcript…' : "I've finished — check for the transcript"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Review view */}
      {interview.status === 'awaiting_review' && draft && (
        <ReviewPanel
          draft={draft}
          interview={interview}
          staff={staff}
          user={user}
          onPublished={() => { load(); onChanged && onChanged(); }}
          onFollowUpCreated={() => { onChanged && onChanged(); }}
        />
      )}

      {/* Published view */}
      {interview.status === 'published' && draft && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={16} color="var(--green, #2e7d32)" /> Published
          </div>
          <div className="card-body">
            <p>This interview's knowledge was published to the internal Knowledge Base.</p>
            {draft.published_path && <p><BookOpen size={14} /> <code>{draft.published_path}</code></p>}
            <details>
              <summary style={{ cursor: 'pointer' }}>View published markdown</summary>
              <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f8fa', padding: 12, borderRadius: 6 }}>{draft.extracted_md}</pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewPanel({ draft, interview, staff, user, onPublished, onFollowUpCreated }) {
  const [md, setMd] = useState(draft.extracted_md || '');
  const [name, setName] = useState(draft.suggested_name || interview.title);
  const [description, setDescription] = useState(draft.suggested_description || '');
  const [folder, setFolder] = useState(draft.suggested_folder || 'references/institutional');
  const [slug, setSlug] = useState(draft.suggested_slug || '');
  const [audienceKey, setAudienceKey] = useState(() => {
    const a = draft.suggested_audience || ['dev'];
    if (a.includes('dev') && a.includes('operator')) return 'both';
    if (a.includes('operator')) return 'operator';
    return 'dev';
  });

  const jimmyCands = Array.isArray(draft.jimmy_candidates) ? draft.jimmy_candidates : [];
  const [jimmyPicks, setJimmyPicks] = useState(() => jimmyCands.map(() => false));
  const [jimmyEdits, setJimmyEdits] = useState(() => jimmyCands.map((c) => ({ ...c })));

  const openQs = Array.isArray(draft.open_questions) ? draft.open_questions : [];
  const [qPicks, setQPicks] = useState(() => openQs.map(() => false));
  const [qAnswers, setQAnswers] = useState(() => openQs.map(() => ''));
  const [qResolved, setQResolved] = useState(() => openQs.map(() => false));
  // Auto-suggest the assignee from the transcript: if a staff member's first name
  // shows up in the open questions (e.g. "ask Rebecca about cooking"), default to them.
  const [followAssignee, setFollowAssignee] = useState(() => {
    const text = openQs.join(' ').toLowerCase();
    const match = (staff || []).find((s) => {
      const first = String(s.staff_name || '').trim().split(/\s+/)[0].toLowerCase();
      return first.length > 2 && new RegExp(`\\b${first}\\b`).test(text);
    });
    return match ? String(match.admin_id) : '';
  });

  // Fold a typed answer straight into the document (most open questions are
  // things you can just answer now; only hand off the ones you truly can't).
  const addAnswerToDoc = (i) => {
    const ans = (qAnswers[i] || '').trim();
    if (!ans) return;
    setMd((prev) => `${prev}\n\n### ${openQs[i]}\n${ans}`);
    setQResolved((prev) => prev.map((v, idx) => (idx === i ? true : v)));
    setQPicks((prev) => prev.map((v, idx) => (idx === i ? false : v)));
    setMsg('Answer added to the document above. Remember to Save draft or Publish.');
  };

  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Fold any typed-but-not-yet-added open-question answers into the document, so
  // Save/Publish never silently drop them. Returns the composed markdown and
  // syncs UI state to match.
  const foldPendingAnswers = () => {
    let out = md;
    const newResolved = [...qResolved];
    const newPicks = [...qPicks];
    let changed = false;
    openQs.forEach((q, i) => {
      if (!qResolved[i] && qAnswers[i] && qAnswers[i].trim()) {
        out += `\n\n### ${q}\n${qAnswers[i].trim()}`;
        newResolved[i] = true; newPicks[i] = false; changed = true;
      }
    });
    if (changed) { setMd(out); setQResolved(newResolved); setQPicks(newPicks); }
    return out;
  };

  const saveDraft = async () => {
    setBusy('save'); setErr(''); setMsg('');
    try {
      const finalMd = foldPendingAnswers();
      await neonQuery('knowledge_draft_save', {
        id: draft.id, extracted_md: finalMd,
        suggested_name: name, suggested_description: description,
        suggested_folder: folder, suggested_slug: slug || null,
        suggested_audience: AUDIENCE_PRESETS.find((a) => a.key === audienceKey)?.value || ['dev'],
        jimmy_candidates: jimmyEdits,
      });
      setMsg('Draft saved.');
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  const followQs = () => openQs.filter((_, i) => qPicks[i] && !qResolved[i]);

  const publish = async () => {
    const finalMd = foldPendingAnswers();
    if (!finalMd.trim()) { setErr('Nothing to publish.'); return; }
    const jimmyItems = jimmyEdits.filter((_, i) => jimmyPicks[i]).filter((it) => it.title && it.content);
    const shareCount = jimmyItems.length;
    const fq = followQs();
    const fa = staff.find((s) => String(s.admin_id) === String(followAssignee));
    const ok = window.confirm(
      'Publish to the internal Knowledge Base'
      + (shareCount ? `, share ${shareCount} snippet(s) with Jimmy` : '')
      + (fq.length ? `, and schedule a follow-up interview${fa ? ` for ${fa.staff_name}` : ''} with ${fq.length} question(s)` : '')
      + '?'
    );
    if (!ok) return;
    setBusy('publish'); setErr(''); setMsg('');
    try {
      const res = await kcPublishDraft({
        draftId: draft.id, markdown: finalMd, name, description, folder, slug: slug || null,
        audience: AUDIENCE_PRESETS.find((a) => a.key === audienceKey)?.value || ['dev'],
        jimmyItems,
        actorAdminId: user?.id != null ? parseInt(user.id, 10) : null,
        actorName: user?.staffName || null,
      });
      // Schedule the follow-up interview for any questions ticked for hand-off.
      let followMsg = '';
      if (fq.length) {
        try {
          await neonQuery('knowledge_interview_create', {
            title: `Follow-up: ${interview.title}`,
            focus: 'Topics deferred to a follow-up during the previous interview.',
            assignee_admin_id: fa ? parseInt(fa.admin_id, 10) : null,
            assignee_name: fa ? fa.staff_name : null,
            assignee_email: fa ? fa.email : null,
            created_by: user?.id != null ? parseInt(user.id, 10) : null,
            created_by_name: user?.staffName || null,
            parent_interview_id: interview.id,
            open_questions: fq,
            source: 'follow_up',
          });
          followMsg = ` · follow-up scheduled${fa ? ` for ${fa.staff_name}` : ''} (${fq.length} question${fq.length > 1 ? 's' : ''})`;
        } catch (_) { followMsg = ' · note: the follow-up could not be scheduled, create it manually'; }
      }
      setMsg(`Published to ${res.path}${res.jimmyInserted ? ` · ${res.jimmyInserted} sent to Jimmy` : ''}${followMsg}.`);
      onPublished();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <>
      {msg && <div className="alert alert-success">{msg}</div>}
      {err && <div className="alert alert-danger">{err}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Review the captured knowledge</div>
        <div className="card-body">
          <p style={{ color: '#889', marginTop: 0 }}>
            Edit anything that's wrong. Watch for <code>[VERIFY]</code> tags — those are facts the AI wasn't sure it heard correctly.
          </p>
          <div className="form-group">
            <label className="form-label">Document</label>
            <textarea className="form-textarea" value={md} onChange={(e) => setMd(e.target.value)} rows={18} style={{ fontFamily: 'monospace', fontSize: 13 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">One-line description</label>
              <input className="form-input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Where it lives</label>
              <select className="form-select" value={folder} onChange={(e) => setFolder(e.target.value)}>
                {FOLDERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Filename (optional)</label>
              <input className="form-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto from title" />
            </div>
          </div>
        </div>
      </div>

      {/* Sensitivity */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Who can see this</div>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label">Internal Knowledge Base audience</label>
            <select className="form-select" value={audienceKey} onChange={(e) => setAudienceKey(e.target.value)} style={{ maxWidth: 320 }}>
              {AUDIENCE_PRESETS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <small style={{ color: '#889', display: 'block', marginTop: 4 }}>
              This controls who sees it in the internal KB and which Claude tools read it. <strong>Jimmy (customers) never sees this</strong> unless you share a snippet below.
            </small>
          </div>

          <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 8 }}>
            <label className="form-label">Share with Jimmy (customer-facing) — opt-in</label>
            {jimmyCands.length === 0 ? (
              <small style={{ color: '#889', display: 'block' }}>The AI found nothing clearly customer-safe to suggest. You can still publish internally above.</small>
            ) : (
              <div>
                <small style={{ color: '#889', display: 'block', marginBottom: 8 }}>
                  Tick only what's safe to tell customers. Checked items get added to Jimmy's knowledge on publish.
                </small>
                {jimmyCands.map((c, i) => (
                  <div key={i} style={{ border: '1px solid #eee', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                      <input type="checkbox" checked={jimmyPicks[i]} onChange={(e) => {
                        const next = [...jimmyPicks]; next[i] = e.target.checked; setJimmyPicks(next);
                      }} />
                      <input className="form-input" style={{ flex: 1 }} value={jimmyEdits[i].title || ''} onChange={(e) => {
                        const next = [...jimmyEdits]; next[i] = { ...next[i], title: e.target.value }; setJimmyEdits(next);
                      }} />
                    </label>
                    <textarea className="form-textarea" rows={2} style={{ marginTop: 6 }} value={jimmyEdits[i].content || ''} onChange={(e) => {
                      const next = [...jimmyEdits]; next[i] = { ...next[i], content: e.target.value }; setJimmyEdits(next);
                    }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Follow-ups */}
      {openQs.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">Open questions</div>
          <div className="card-body">
            <p style={{ color: '#889', marginTop: 0 }}>
              The AI pulled these from your interview — the topics you deferred or left open. For each one,
              either <strong>answer it yourself</strong> (type below, it folds into the document), or
              <strong> tick "Send to a follow-up interview"</strong> for the ones meant for someone else.
              Ticked questions become one follow-up interview, assigned and emailed, the moment you Publish.
            </p>
            {openQs.map((q, i) => (
              <div key={i} style={{ border: '1px solid #eee', borderRadius: 6, padding: 10, marginBottom: 8, background: qResolved[i] ? '#f3faf3' : (qPicks[i] ? '#fff6ef' : '#fff') }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ flex: 1, fontWeight: 500 }}>
                    {qResolved[i] && <CheckCircle size={14} color="#2e7d32" style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                    {q}
                  </span>
                </div>
                {qResolved[i] ? (
                  <div style={{ fontSize: 12, color: '#2e7d32', marginTop: 6 }}>Answer added to the document above.</div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      placeholder="Type your answer here…"
                      value={qAnswers[i]}
                      onChange={(e) => { const next = [...qAnswers]; next[i] = e.target.value; setQAnswers(next); }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '4px 12px' }}
                        disabled={!qAnswers[i] || !qAnswers[i].trim()}
                        onClick={() => addAnswerToDoc(i)}
                      >
                        <Save size={14} /> Add answer to document
                      </button>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: qPicks[i] ? 'var(--orange)' : '#556', cursor: 'pointer' }}>
                        <input type="checkbox" checked={qPicks[i]} onChange={(e) => { const next = [...qPicks]; next[i] = e.target.checked; setQPicks(next); }} />
                        Send to a follow-up interview
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {followQs().length > 0 && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 10, flexWrap: 'wrap', borderTop: '1px solid #eee', paddingTop: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Follow-up for {followQs().length} question{followQs().length > 1 ? 's' : ''} — assign to</label>
                  <select className="form-select" value={followAssignee} onChange={(e) => setFollowAssignee(e.target.value)}>
                    <option value="">Me ({user?.staffName || 'current user'})</option>
                    {staff.map((s) => <option key={s.admin_id} value={s.admin_id}>{s.staff_name}</option>)}
                  </select>
                </div>
                <small style={{ color: '#889', paddingBottom: 6 }}>
                  <UserPlus size={13} style={{ verticalAlign: 'middle' }} /> Created and emailed when you click <strong>Publish</strong>.
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-outline" disabled={busy === 'save'} onClick={saveDraft}>
          <Save size={16} /> {busy === 'save' ? 'Saving…' : 'Save draft'}
        </button>
        <button className="btn btn-primary" disabled={busy === 'publish'} onClick={publish}>
          <Send size={16} /> {busy === 'publish' ? 'Publishing…' : 'Publish to Knowledge Base'}
        </button>
      </div>
    </>
  );
}
